import { Link } from 'react-router-dom';
import {
  Brain,
  MessageSquare,
  Users,
  Upload,
  Share2,
  BookOpen,
  Sparkles,
  Target,
  Zap,
  Star,
  Check,
  ArrowRight,
  ChevronRight
} from 'lucide-react';

function Landing() {
  const features = [
    {
      icon: Brain,
      title: 'AI Memory Tutor',
      description: 'Get personalized guidance on memory techniques like Memory Palace, Peg System, and more from our AI coach.'
    },
    {
      icon: BookOpen,
      title: 'Technique-Specific Training',
      description: 'Dedicated chat areas for each memory technique, helping you master one method at a time.'
    },
    {
      icon: Users,
      title: 'Community Channels',
      description: 'Connect with trainers and fellow students in open discussion channels and broadcasts.'
    },
    {
      icon: MessageSquare,
      title: 'Direct Messaging',
      description: 'Private conversations with your trainer for personalized feedback and support.'
    },
    {
      icon: Upload,
      title: 'Image & PDF Support',
      description: 'Upload images and documents to get AI help with memorizing their contents.'
    },
    {
      icon: Share2,
      title: 'Share Your Progress',
      description: 'Create shareable links of your chat sessions to showcase your learning journey.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Start Your Free Trial',
      description: 'Sign up and get 7 days of free access with 5 questions per day to explore.'
    },
    {
      number: '02',
      title: 'Chat with AI Coach',
      description: 'Ask questions about memory techniques and get personalized guidance instantly.'
    },
    {
      number: '03',
      title: 'Master Your Memory',
      description: 'Practice techniques, track your progress, and unlock your memory potential.'
    }
  ];

  const testimonials = [
    {
      name: 'Priya Sharma',
      role: 'Medical Student',
      content: 'Memory Coach transformed how I study. I can now memorize complex anatomy terms using the Memory Palace technique. My exam scores have improved significantly!',
      rating: 5
    },
    {
      name: 'Rahul Verma',
      role: 'Competitive Exam Aspirant',
      content: 'The AI tutor explains concepts so clearly. I learned the Major System in just a week and can now memorize long number sequences easily.',
      rating: 5
    },
    {
      name: 'Ananya Patel',
      role: 'College Student',
      content: 'Having a trainer in the community channels really helps. The discussion mode lets me learn from others questions too. Great platform!',
      rating: 5
    }
  ];

  const packages = [
    {
      name: 'Basic',
      price: 100,
      questions: 200,
      popular: false
    },
    {
      name: 'Standard',
      price: 200,
      questions: 400,
      popular: true
    },
    {
      name: 'Premium',
      price: 500,
      questions: 1000,
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <Brain className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">Memory Coach</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Register
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-primary-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              AI-Powered Memory Training
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Unlock Your Brain's
              <span className="text-primary-600"> Full Potential</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Master proven memory techniques with your personal AI coach. Learn Memory Palace,
              Peg System, Major System and more through interactive conversations.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors shadow-lg shadow-primary-600/20"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors border border-gray-200"
              >
                Sign In
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              7-day free trial with 5 questions/day. No credit card required.
            </p>
          </div>

          {/* Hero Visual */}
          <div className="mt-16 relative">
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl shadow-2xl p-8 max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-inner">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      To create a Memory Palace, start by choosing a familiar location like your home.
                      Walk through it mentally and identify 5-10 distinct spots. Let's begin -
                      what location would you like to use?
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 flex-row-reverse">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 ml-4">
                    <span className="text-sm font-medium text-gray-600">You</span>
                  </div>
                  <div className="flex-1 bg-primary-50 rounded-lg p-4">
                    <p className="text-gray-700">
                      I want to use my apartment. It has a living room, kitchen, bedroom, and balcony.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything You Need to
              <span className="text-primary-600"> Master Memory</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              A complete platform for learning memory techniques with AI guidance,
              community support, and personalized training.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg hover:border-primary-100 transition-all group"
              >
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-100 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                  <div className="text-5xl font-bold text-primary-100 mb-4">{step.number}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ChevronRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Loved by Students
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              See what our learners have to say
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6">"{testimonial.content}"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-700 font-semibold">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Pay only for what you use. No subscriptions, no hidden fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl p-8 shadow-sm border-2 transition-all ${
                  pkg.popular
                    ? 'border-primary-500 shadow-lg scale-105'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">Rs. {pkg.price}</span>
                </div>
                <p className="text-gray-600 mb-6">{pkg.questions} questions</p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-600">
                    <Check className="w-5 h-5 text-primary-600 mr-2" />
                    AI Memory Coach access
                  </li>
                  <li className="flex items-center text-gray-600">
                    <Check className="w-5 h-5 text-primary-600 mr-2" />
                    All memory techniques
                  </li>
                  <li className="flex items-center text-gray-600">
                    <Check className="w-5 h-5 text-primary-600 mr-2" />
                    Community access
                  </li>
                </ul>
                <Link
                  to="/register"
                  className={`block text-center py-3 rounded-xl font-semibold transition-colors ${
                    pkg.popular
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Register
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8">
            Rs. 0.50 per question average. Credits valid for 30 days.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl p-12 text-center shadow-xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Transform Your Memory?
            </h2>
            <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of students who are unlocking their brain's potential with
              Memory Coach. Start your free trial today.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold hover:bg-primary-50 transition-colors shadow-lg"
            >
              Start Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Brain className="w-8 h-8 text-primary-500" />
              <span className="text-xl font-bold text-white">Memory Coach</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/login" className="hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/register" className="hover:text-white transition-colors">
                Register
              </Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} BrainathonHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
